import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EnvelopeIcon, EyeIcon, TrashIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { fetchContacts, updateContact, deleteContact } from '../../store/slices/contactSlice';
import { showSuccess, showError } from '../../store/slices/notificationSlice';
import { Table, SearchInput, Pagination, ConfirmDialog, Card, Modal, DynamicBadge, DynamicSelect, Badge } from '../../components/Common';
import { useOptions } from '../../hooks/useOptions';

const ContactList = () => {
  const dispatch = useDispatch();
  const { contacts, loading, pagination } = useSelector((state) => state.contact);
  
  // Dynamic status options are used in the DynamicSelect component below
  useOptions('contactStatus');
  
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [viewModal, setViewModal] = useState({ open: false, contact: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  useEffect(() => {
    dispatch(fetchContacts({ page, size: pageSize, status: statusFilter }));
  }, [dispatch, page, pageSize, statusFilter]);

  const handleStatusChange = async (id, status) => {
    try {
      await dispatch(updateContact({ id, data: { status } })).unwrap();
      dispatch(showSuccess('Contact status updated'));
      dispatch(fetchContacts({ page, size: pageSize, status: statusFilter }));
    } catch (error) {
      dispatch(showError(error || 'Failed to update status'));
    }
  };

  const handleDelete = async () => {
    if (deleteModal.id) {
      try {
        await dispatch(deleteContact(deleteModal.id)).unwrap();
        dispatch(showSuccess('Contact deleted successfully'));
        dispatch(fetchContacts({ page, size: pageSize, status: statusFilter }));
      } catch (error) {
        dispatch(showError(error || 'Failed to delete contact'));
      }
      setDeleteModal({ open: false, id: null });
    }
  };

  // Dynamic status options are used in the DynamicSelect component below

  const formSourceLabel = (source) => {
    if (!source || source === 'website') return 'Contact Form';
    if (source === 'subscription') return 'Subscription Form';
    return source;
  };

  const columns = [
    {
      key: 'name',
      title: 'Contact',
      render: (value, row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
            <span className="text-amber-600 font-semibold">{value?.charAt(0)?.toUpperCase() || '?'}</span>
          </div>
          <div>
            <p className="font-medium text-slate-800">{value || 'Unknown'}</p>
            <p className="text-sm text-slate-500">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'source',
      title: 'Source',
      render: (value) => (
        <Badge variant={value === 'subscription' ? 'info' : 'default'}>
          {formSourceLabel(value)}
        </Badge>
      ),
    },
    {
      key: 'message',
      title: 'Message',
      render: (value) => <p className="text-slate-600 line-clamp-2 max-w-xs">{value || 'No message'}</p>,
    },
    {
      key: 'priority',
      title: 'Priority',
      render: (value) => <DynamicBadge optionType="contactPriority" value={value} />,
    },
    {
      key: 'status',
      title: 'Status',
      render: (value) => <DynamicBadge optionType="contactStatus" value={value} dot />,
    },
    {
      key: 'created_at',
      title: 'Received',
      render: (value) => (
        <div className="flex items-center gap-1 text-slate-500 text-sm">
          <ClockIcon className="w-4 h-4" />
          {value ? new Date(value).toLocaleDateString() : '-'}
        </div>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <button onClick={() => setViewModal({ open: true, contact: row })} className="p-2 hover:bg-slate-100 rounded-lg" title="View">
            <EyeIcon className="w-4 h-4 text-slate-500" />
          </button>
          {row.status !== 'resolved' && (
            <button onClick={() => handleStatusChange(row.id, 'resolved')} className="p-2 hover:bg-emerald-50 rounded-lg" title="Mark Resolved">
              <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
            </button>
          )}
          <button onClick={() => setDeleteModal({ open: true, id: row.id })} className="p-2 hover:bg-red-50 rounded-lg" title="Delete">
            <TrashIcon className="w-4 h-4 text-red-500" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Form Submissions</h1>
          <p className="text-slate-500">Manage contact and subscription form submissions</p>
        </div>
      </div>

      <Card noPadding>
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row items-center gap-4">
          <SearchInput value={search} onChange={setSearch} placeholder="Search form submissions..." className="flex-1 max-w-md" />
          <DynamicSelect 
            optionType="contactStatus" 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)} 
            placeholder="All Status"
            className="w-40" 
          />
        </div>

        <Table columns={columns} data={contacts} loading={loading} emptyMessage="No form submissions found" emptyIcon={EnvelopeIcon} />

        {pagination && pagination.totalPages > 1 && (
          <Pagination currentPage={page} totalPages={pagination.totalPages} totalItems={pagination.total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
        )}
      </Card>

      {/* View Modal */}
      <Modal isOpen={viewModal.open} onClose={() => setViewModal({ open: false, contact: null })} title="Contact Details" size="md">
        {viewModal.contact && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                <span className="text-amber-600 font-bold text-2xl">{viewModal.contact.name?.charAt(0)?.toUpperCase()}</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">{viewModal.contact.name}</h3>
                <p className="text-slate-500">{viewModal.contact.email}</p>
                {viewModal.contact.phone && <p className="text-slate-500">{viewModal.contact.phone}</p>}
              </div>
            </div>
            <div className="flex gap-2">
              <DynamicBadge optionType="contactStatus" value={viewModal.contact.status} dot />
              <DynamicBadge optionType="contactPriority" value={viewModal.contact.priority} />
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm text-slate-600">
              <div>
                <span className="text-slate-400">Source:</span>{' '}
                <span className="font-medium">{formSourceLabel(viewModal.contact.source)}</span>
              </div>
              <div>
                <span className="text-slate-400">Category:</span>{' '}
                <span className="font-medium capitalize">{viewModal.contact.category || 'General'}</span>
              </div>
              {viewModal.contact.subject && (
                <div className="col-span-2">
                  <span className="text-slate-400">Subject:</span>{' '}
                  <span className="font-medium">{viewModal.contact.subject}</span>
                </div>
              )}
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <h4 className="font-medium text-slate-700 mb-2">Message</h4>
              <p className="text-slate-600 whitespace-pre-wrap">{viewModal.contact.message}</p>
            </div>
            <div className="text-sm text-slate-500">
              Received: {viewModal.contact.created_at ? new Date(viewModal.contact.created_at).toLocaleString() : 'Unknown'}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog isOpen={deleteModal.open} onClose={() => setDeleteModal({ open: false, id: null })} onConfirm={handleDelete} title="Delete Contact" message="Are you sure you want to delete this contact message?" type="danger" confirmText="Delete" />
    </div>
  );
};

export default ContactList;
